import React, { useState, useEffect, useContext, useRef } from "react";
import { AppBar, Toolbar, Grid, Box, Typography, Button } from "@mui/material";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useLocation } from "react-router-dom";
import * as api from "../../api/Defect";
import * as dict from "../../dictionary";
import { ProfileContext } from "../../contexts/ProfileContext";
import { CardResponse, SignaturePad } from "../../ui-component/index";
import { BackDropLoading } from "../../ui-component/index";
import { checkBase64 } from "../../utils/checkBase64";
import { ModalError } from "../../ui-component/index";

export const DefectSignature = (props) => {
    const domain = window.location.origin
    const isFastCare = props?.version === "fastcare"

    if (isFastCare) {
        document.title = "Fast Care"
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = domain + "/fast-favicon.ico";
    }
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const [modalError, setModalError] = useState({
    open: false,
    error: {
      status: "",
      message: "",
    },
  });
  const [alertSuccess, setAlertSuccess] = useState({
    open: false,
    title: "",
  });

  const context = useContext(ProfileContext);
  const sigPadRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [signatureDate, setSignatureDate] = useState(null);
  const [signatureImage, setSignatureImage] = useState(null);

  var params = new URLSearchParams(location.search);
  var unitText = params.get("unitText");
  var defectId = params.get("id");
  var projectInfo;

  if (checkBase64(unitText)) {
    projectInfo = window.atob(unitText);
  }

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const handleAcceptTask = async (signatureImage) => {
    const signatureBlob = dataURItoBlob(signatureImage);
    const signatureFile = new File([signatureBlob], 'customer_signature_file.png', { type: 'image/png' });

    const formData = new FormData();
    formData.append('defect_id', parseInt(defectId));
    formData.append('new_defect_status', "P");
    formData.append('remark', "");
    formData.append('customer_signature_file', signatureFile);

    let res = await api.saveDefectStatus(formData, projectInfo?.split("|")[1]);
    if (res.status === 200) {
      return setAlertSuccess({
        open: true,
        title:
          dict.PASS_SAVE_ACCEPT_REPAIR_TXT[
            profile?.language || sessionStorage.getItem("fast-lang") || "th"
          ],
      });
    }

    return setModalError({
      open: true,
      error: res,
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const pad = (num) => num.toString().padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  const handleSaveSignature = async () => {
    if (!sigPadRef.current) return;

    const img = sigPadRef.current.save();
    if (!img) return;

    setSignatureImage(img);
    const now = new Date();
    setSignatureDate(now);

    setLoading(true);
    await handleAcceptTask(img);
    setLoading(false);
  };

  const handleResetSignature = () => {
    sigPadRef.current?.clear();
    setIsSigned(false);
    setSignatureImage(null);
    setSignatureDate(null);
  };

  const handleDialogSuccessClose = () => {
    setAlertSuccess({
      open: false,
      title: "",
    });
    navigate(`/fastcare/detail${location.search}`);
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    if (context.state?.profile !== null) {
      setProfile(context.state?.profile?.customer[0]);
    }
    setLoading(false);
  }, [context]);

  return (
    <>
      <AppBar
        sx={{
          position: "fixed",
          boxShadow: "none",
          height: 60,
        }}
      >
        <Toolbar>
          <Grid container item xs={1}>
            <ArrowBackIos
              onClick={() => navigate(`/fastcare/detail${location.search}`)}
            />
          </Grid>
          <Grid container item xs={10} direction="row" justifyContent="center">
            <Typography>เซ็นชื่อ</Typography>
          </Grid>
        </Toolbar>
      </AppBar>
      <Box sx={{ height: "calc(100dvh)", mt: "60px", px: 2 }}>
        <Typography sx={{ mt: 10, fontSize: 16, fontWeight: 600 }}>กรุณาเซ็นชื่อเพื่อยอมรับงานแจ้งซ่อมนี้</Typography>

        <Box
          sx={{
            mt: 2,
            position: "relative",
          }}
        >
          <SignaturePad
            ref={sigPadRef}
            showActions={false}
            onChange={(hasSignature) => setIsSigned(hasSignature)}
            onSave={(img) => setSignatureImage(img)}
          />
        </Box>

        <Typography sx={{ mt: 2, fontWeight: 500 }}>
          ลงชื่อ วันที่ : {signatureDate ? formatDateTime(signatureDate) : "-"}
        </Typography>

        <Box sx={{ mt: 2, display: "grid", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#00a65a",
              color: "#fff",
              border: "2px solid #000",
              fontWeight: 700,
              fontSize: 16,
              height: 50,
              '&:hover': { backgroundColor: '#02b86c' },
            }}
            onClick={handleSaveSignature}
            disabled={!isSigned}
          >
            บันทึก
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{
              color: "#000",
              backgroundColor: "#ffcccc",
              border: "2px solid #000",
              fontWeight: 700,
              fontSize: 16,
              height: 50,
              '&:hover': { backgroundColor: '#ff9999' },
            }}
            onClick={handleResetSignature}
          >
            เซ็นชื่อใหม่
          </Button>
        </Box>
      </Box>

      

      <CardResponse
        type={"success"}
        titleDescription={alertSuccess.title}
        open={alertSuccess.open}
        onClose={handleDialogSuccessClose}
        onOk={handleDialogSuccessClose}
      />

      <ModalError
        onClose={() =>
          setModalError({
            open: false,
            error: {
              status: "",
              message: "",
            },
          })
        }
        open={modalError.open}
        error={modalError.error}
      />

      <BackDropLoading loading={loading} />
    </>
  );
};
